import type { Flashcard, Lesson, Question, Skill, Topic } from '@/data/types';
import { card, choice, example, f, numeric, p, text, tip, warn } from '../../authoring';

/**
 * Biznes i zarządzanie, dział 5: przedsiębiorstwo i biznesplan.
 *
 * Podstawa programowa 2024: ZP VI.3–VI.8, VI.10; ZR II.15, V.1–V.12,
 * V.20, V.21, V.23–V.25. Stawki podatków w zadaniach podane są w treści.
 */

export const FIRM_TOPIC: Topic = {
  id: 'biz-firm-topic',
  subjectId: 'biz',
  name: 'Przedsiębiorstwo i biznesplan',
  summary: 'Rodzaje i formy prawne firm, opodatkowanie działalności, pomysł i model biznesu, analiza otoczenia i SWOT, rentowność i próg rentowności, finansowanie i ryzyko.',
};

export const FIRM_SKILLS: Skill[] = [
  {
    id: 'biz-enterprise-types',
    topicId: 'biz-firm-topic',
    name: 'Rodzaje, formy prawne i opodatkowanie firm',
    level: 'PR',
    ckeRequirement: 'Klasyfikacja przedsiębiorstw, innowacyjne modele (startupy, przedsiębiorstwa społeczne, spółdzielnie), formy opodatkowania działalności gospodarczej (ZR V.1, II.15)',
    prerequisites: ['biz-fiscal-monetary'],
    examValue: 0.85,
  },
  {
    id: 'biz-business-model',
    topicId: 'biz-firm-topic',
    name: 'Pomysł, badania rynku i model biznesu',
    level: 'PR',
    ckeRequirement: 'Pomysł na przedsięwzięcie, badania rynkowe, profil klienta, propozycja wartości, MVP, Business Model Canvas, biznesplan (ZP VI.3, VI.4, VI.10; ZR V.2–V.4, V.6–V.8, V.12, V.24, V.25)',
    prerequisites: ['biz-enterprise-types'],
    examValue: 0.8,
  },
  {
    id: 'biz-environment-swot',
    topicId: 'biz-firm-topic',
    name: 'Otoczenie firmy, SWOT i przewaga konkurencyjna',
    level: 'PR',
    ckeRequirement: 'Mikro- i makrootoczenie (PEST), analiza SWOT, zasoby, przewaga konkurencyjna, ochrona własności intelektualnej (ZP VI.5, VI.6; ZR V.5, V.9–V.11)',
    prerequisites: ['biz-business-model'],
    examValue: 0.85,
  },
  {
    id: 'biz-profitability',
    topicId: 'biz-firm-topic',
    name: 'Przychody, koszty i próg rentowności',
    level: 'PR',
    ckeRequirement: 'Prognoza efektów finansowych, rentowność produktu, próg rentowności i ich interpretacja (ZP VI.8; ZR V.20)',
    prerequisites: ['biz-environment-swot'],
    examValue: 0.95,
  },
  {
    id: 'biz-financing-risk',
    topicId: 'biz-firm-topic',
    name: 'Finansowanie i ryzyko przedsięwzięcia',
    level: 'PR',
    ckeRequirement: 'Źródła finansowania (instytucje finansowe, urzędy pracy, fundusze UE, venture capital, anioły biznesu), inkubatory, czynniki ryzyka (ZP VI.7; ZR V.21, V.23)',
    prerequisites: ['biz-profitability'],
    examValue: 0.8,
  },
];

// ===========================================================================
// Lekcje
// ===========================================================================

export const FIRM_LESSONS: Lesson[] = [
  {
    skillId: 'biz-enterprise-types',
    minutes: 16,
    intro:
      'Zanim założysz firmę, wybierasz jej formę prawną. Od niej zależy, czy za długi firmy odpowiadasz własnym mieszkaniem, ile formalności cię czeka i jak płacisz podatki.',
    blocks: [
      p('Klasyfikacje: według rodzaju działalności (produkcyjne, handlowe, usługowe), celu (komercyjne — zysk; społeczne — cel społeczny, np. spółdzielnia socjalna), wielkości (mikro: do 9 pracowników, małe: do 49, średnie: do 249, duże — od 250, z limitami obrotu), formy własności (prywatne, publiczne). Startup to młoda firma szukająca powtarzalnego, skalowalnego modelu biznesu, zwykle innowacyjnego.'),
      p('Formy prawne: jednoosobowa działalność gospodarcza (właściciel odpowiada całym majątkiem), spółka cywilna (umowa wspólników, solidarna odpowiedzialność), spółka jawna (wspólnicy odpowiadają całym majątkiem, gdy majątek spółki nie wystarczy), spółka z o.o. (kapitał zakładowy min. 5000 zł, wspólnicy co do zasady nie odpowiadają osobiście za długi spółki), spółka akcyjna (kapitał min. 100 000 zł, akcje, możliwość wejścia na giełdę).'),
      p('Opodatkowanie działalności osoby fizycznej: skala podatkowa, podatek liniowy (jedna stawka od dochodu), ryczałt od przychodów ewidencjonowanych (stawka od przychodu, bez odliczania kosztów). Spółki kapitałowe płacą CIT.'),
      f('\\text{dochód} = \\text{przychód} - \\text{koszty} \\qquad \\text{liniowy: } t \\cdot \\text{dochód} \\qquad \\text{ryczałt: } t \\cdot \\text{przychód}'),
      tip('Ryczałt opłaca się firmom o niskich kosztach — liczy się go od przychodu, więc koszty nie obniżają podatku.'),
      warn('W spółce z o.o. za jej długi odpowiada majątek spółki — ale zarząd może odpowiadać osobiście, jeśli w porę nie zgłosił upadłości.'),
    ],
    examples: [
      example(
        'Przychód 200 000 zł, koszty 120 000 zł. Porównaj ryczałt 8,5% z podatkiem liniowym 19% (w uproszczeniu).',
        [['Ryczałt: 8,5% · 200 000 = 17 000 zł.', 'od przychodu'], 'Liniowy: 19% · 80 000 = 15 200 zł — taniej o 1800 zł.'],
        'liniowy tańszy o 1800 zł',
      ),
      example(
        'Kto odpowiada za długi jednoosobowej działalności gospodarczej?',
        ['Firma nie jest odrębną osobą prawną.', 'Odpowiada właściciel — całym majątkiem osobistym.'],
        'właściciel, całym majątkiem',
      ),
    ],
    pitfalls: ['Liczenie ryczałtu od dochodu zamiast od przychodu.', 'Przekonanie, że w spółce jawnej wspólnicy nie ryzykują własnym majątkiem.', 'Mylenie przedsiębiorstwa społecznego z organizacją charytatywną bez działalności gospodarczej.'],
  },
  {
    skillId: 'biz-business-model',
    minutes: 15,
    intro:
      'Dobry pomysł na biznes zaczyna się od problemu klienta, a nie od produktu. Zanim wydasz oszczędności, sprawdź, kto ma ten problem, ile zapłaci za rozwiązanie i jak do niego dotrzesz.',
    blocks: [
      p('Badania rynku: pierwotne (ankiety, wywiady, obserwacja, tajemniczy klient — ktoś udaje klienta konkurencji, żeby ocenić obsługę) i wtórne (raporty, dane GUS). Benchmarking to porównywanie się z najlepszymi. Na tej podstawie tworzy się profil klienta (personę) i propozycję wartości — zestaw korzyści, który rozwiązuje jego problem.'),
      p('MVP (Minimum Viable Product) to najprostsza wersja produktu, która pozwala sprawdzić, czy klienci go chcą — zanim zbudujesz pełną wersję. Informacja zwrotna od klientów służy do ulepszania pomysłu.'),
      p('Business Model Canvas opisuje model biznesu na jednej stronie w 9 blokach: segmenty klientów, propozycja wartości, kanały dotarcia, relacje z klientami, strumienie przychodów, kluczowe zasoby, kluczowe działania, kluczowi partnerzy, struktura kosztów. Biznesplan to szczegółowy dokument: opis przedsięwzięcia, analiza rynku i konkurencji, plan marketingowy, organizacja, plan finansowy, analiza ryzyka.'),
      tip('Propozycja wartości odpowiada na pytanie: dlaczego klient wybierze nas, a nie konkurencję?'),
      warn('MVP nie jest „byle jakim produktem” — to świadomie okrojona wersja, która ma szybko dać wiedzę o klientach.'),
    ],
    examples: [
      example(
        'Właścicielka kawiarni zatrudnia osobę, która odwiedza kawiarnie konkurencji jako klient i ocenia obsługę. Jaka to metoda badań?',
        [['Badacz udaje zwykłego klienta.', 'obserwacja w roli klienta'], 'To metoda tajemniczego klienta.'],
        'tajemniczy klient',
      ),
      example(
        'Do którego bloku Business Model Canvas należy „płatność w abonamencie miesięcznym”?',
        ['To sposób, w jaki firma zarabia.', 'Blok: strumienie przychodów.'],
        'strumienie przychodów',
      ),
    ],
    pitfalls: ['Zaczynanie od produktu zamiast od problemu klienta.', 'Mylenie propozycji wartości z ceną.', 'Traktowanie MVP jako produktu końcowego.'],
  },
  {
    skillId: 'biz-environment-swot',
    minutes: 15,
    intro:
      'Firma działa w otoczeniu, na które ma mały wpływ (prawo, gospodarka, technologia), i w otoczeniu bliskim (klienci, dostawcy, konkurenci). Analiza SWOT zestawia to z tym, co firma ma w środku.',
    blocks: [
      p('Makrootoczenie analizuje się metodą PEST (lub PESTEL): czynniki polityczno-prawne, ekonomiczne, społeczne, technologiczne (oraz środowiskowe i prawne osobno). Mikrootoczenie to klienci, dostawcy, konkurenci, pośrednicy, lokalna społeczność.'),
      p('SWOT: mocne strony (Strengths) i słabe strony (Weaknesses) to czynniki WEWNĘTRZNE — zależą od firmy (np. kompetencje zespołu, sprzęt). Szanse (Opportunities) i zagrożenia (Threats) to czynniki ZEWNĘTRZNE (np. nowe przepisy, moda, wejście konkurenta). Wnioski: wykorzystuj szanse mocnymi stronami, wzmacniaj słabe, minimalizuj zagrożenia.'),
      p('Przewaga konkurencyjna to trwała cecha, dzięki której klienci wybierają firmę: niższy koszt, wyjątkowa jakość, marka, technologia. Chroni ją własność intelektualna: patent (wynalazek), wzór użytkowy i przemysłowy (rozwiązanie techniczne, wygląd), znak towarowy (logo, nazwa), prawo autorskie (utwory, programy komputerowe — powstaje automatycznie).'),
      tip('Test SWOT: czy firma ma na ten czynnik wpływ? Tak — mocna lub słaba strona. Nie — szansa lub zagrożenie.'),
      warn('„Rosnąca popularność zdrowej żywności” to szansa (zewnętrzna), a nie mocna strona — nawet jeśli firma sprzedaje zdrową żywność.'),
    ],
    examples: [
      example(
        'Piekarnia ma doświadczonego piekarza, a w okolicy otwiera się duży market z tanim pieczywem. Sklasyfikuj oba czynniki w SWOT.',
        [['Piekarz — czynnik wewnętrzny, pozytywny.', 'mocna strona'], 'Market — czynnik zewnętrzny, negatywny: zagrożenie.'],
        'mocna strona i zagrożenie',
      ),
      example(
        'Jak chronić logo i nazwę nowej marki napojów?',
        ['Oznaczenia odróżniające towary chroni znak towarowy.', 'Zgłasza się go w Urzędzie Patentowym.'],
        'rejestracja znaku towarowego',
      ),
    ],
    pitfalls: ['Wpisywanie czynników zewnętrznych jako mocnych stron.', 'Mylenie patentu ze znakiem towarowym.', 'Przekonanie, że prawo autorskie wymaga rejestracji.'],
  },
  {
    skillId: 'biz-profitability',
    minutes: 17,
    intro:
      'Czy biznes zarobi? Odpowiedź daje porównanie przychodów z kosztami. Kluczowe pojęcie to próg rentowności — liczba sprzedanych sztuk, przy której firma wychodzi na zero.',
    blocks: [
      p('Koszty stałe nie zależą od wielkości produkcji (czynsz, pensje administracji, leasing). Koszty zmienne rosną z każdą sztuką (surowiec, opakowanie, prowizja). Marża jednostkowa (na pokrycie kosztów stałych) to cena minus koszt zmienny jednej sztuki.'),
      f('Q_{BEP} = \\frac{K_{\\text{stałe}}}{c - k_{zm}} \\qquad \\text{zysk} = Q \\cdot (c - k_{zm}) - K_{\\text{stałe}}','Próg rentowności ilościowy i zysk przy sprzedaży Q sztuk.'),
      f('\\text{rentowność sprzedaży} = \\frac{\\text{zysk}}{\\text{przychody}} \\cdot 100\\%'),
      p('Próg rentowności wartościowy to przychód na progu: $Q_{BEP} \\cdot c$. Powyżej progu każda sprzedana sztuka dokłada do zysku marżę jednostkową. Żeby osiągnąć zakładany zysk Z, trzeba sprzedać $\\frac{K_{\\text{stałe}} + Z}{c - k_{zm}}$ sztuk.'),
      tip('Sprawdzaj jednostki: koszty stałe za miesiąc dają próg w sztukach na miesiąc.'),
      warn('Przy cenie niższej lub równej kosztowi zmiennemu próg rentowności nie istnieje — każda sprzedaż pogłębia stratę.'),
    ],
    examples: [
      example(
        'Koszty stałe: 12 000 zł miesięcznie, cena: 50 zł, koszt zmienny: 20 zł/szt. Oblicz próg rentowności.',
        [['Marża jednostkowa: 50 − 20 = 30 zł.', 'każda sztuka pokrywa 30 zł kosztów stałych'], '12 000 : 30 = 400 sztuk miesięcznie.'],
        '400 sztuk',
      ),
      example(
        'Ile wyniesie zysk przy sprzedaży 600 sztuk (te same dane)?',
        ['600 · 30 = 18 000 zł marży.', '18 000 − 12 000 = 6000 zł zysku.'],
        '6000 zł',
      ),
    ],
    pitfalls: ['Mylenie kosztów stałych ze zmiennymi.', 'Dzielenie kosztów stałych przez cenę zamiast przez marżę jednostkową.', 'Liczenie rentowności od kosztów zamiast od przychodów.'],
  },
  {
    skillId: 'biz-financing-risk',
    minutes: 13,
    intro:
      'Każdy biznes potrzebuje pieniędzy na start i rozwój. Źródło finansowania wpływa na to, ile oddasz, komu i czy będziesz musiał dzielić się kontrolą nad firmą.',
    blocks: [
      p('Źródła własne: oszczędności, wkłady wspólników, zysk zatrzymany w firmie. Obce: kredyt i pożyczka (trzeba spłacić z odsetkami), leasing (korzystanie z maszyny lub auta za raty), dotacje (z urzędu pracy dla bezrobotnych, fundusze UE — zwykle bezzwrotne pod warunkami), crowdfunding (wiele drobnych wpłat od internautów).'),
      p('Kapitał podwyższonego ryzyka: anioł biznesu (zamożna osoba prywatna, często mentor) i fundusz venture capital inwestują w obiecujące młode firmy w zamian za udziały — nie trzeba im spłacać rat, ale oddaje się część własności i zysków. Inkubatory przedsiębiorczości dają młodym firmom tanie biuro, doradztwo i kontakty.'),
      p('Ryzyko ocenia się przez prawdopodobieństwo wystąpienia i siłę wpływu (macierz ryzyka). Na ryzyka o dużym prawdopodobieństwie i dużym wpływie trzeba mieć plan: unikanie, ograniczanie, przeniesienie (np. ubezpieczenie) albo świadoma akceptacja.'),
      tip('Oczekiwana strata = prawdopodobieństwo · skutek finansowy — pozwala porównać ryzyka.'),
      warn('Inwestor venture capital nie jest darczyńcą — oczekuje dużego zwrotu i ma wpływ na decyzje firmy.'),
    ],
    examples: [
      example(
        'Które źródło finansowania nie wymaga spłaty, ale oznacza oddanie części udziałów w firmie?',
        [['Kredyt trzeba spłacić, dotacja nie daje nikomu udziałów.', 'wymiana kapitału na udziały'], 'Tak działa anioł biznesu lub fundusz VC.'],
        'anioł biznesu / venture capital',
      ),
      example(
        'Ryzyko A: prawdopodobieństwo 40%, strata 50 000 zł. Ryzyko B: 10%, strata 200 000 zł. Które ma wyższą oczekiwaną stratę?',
        ['A: 0,4 · 50 000 = 20 000 zł.', 'B: 0,1 · 200 000 = 20 000 zł — tyle samo.'],
        'równe',
      ),
    ],
    pitfalls: ['Traktowanie dotacji jako darmowych pieniędzy bez warunków.', 'Pomijanie kosztu utraty udziałów przy VC.', 'Ocena ryzyka tylko po prawdopodobieństwie.'],
  },
];

// ===========================================================================
// Zadania
// ===========================================================================

export const FIRM_QUESTIONS: Question[] = [
  // biz-enterprise-types ------------------------------------------------------
  choice({
    id: 'bp-t-1',
    skill: 'biz-enterprise-types',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Kto odpowiada za zobowiązania jednoosobowej działalności gospodarczej?',
    choices: ['właściciel — całym swoim majątkiem, także prywatnym', 'wyłącznie majątek firmy, bez majątku prywatnego właściciela', 'Skarb Państwa', 'bank prowadzący rachunek firmy'],
    answer: 'A',
    hints: ['Czy jednoosobowa działalność jest odrębną osobą prawną?', 'Nie — to przedsiębiorca działający pod własnym nazwiskiem.', 'Czyj majątek jest więc zagrożony?', 'Całym majątkiem odpowiada właściciel.'],
    steps: ['Firma jednoosobowa nie jest oddzielona od właściciela.', 'Za długi odpowiada on całym majątkiem, także prywatnym.'],
    errors: [
      ['B', 'Tak jest co do zasady w spółce z o.o.', 'W JDG odpowiada właściciel całym majątkiem.'],
      ['C', 'Państwo nie odpowiada za długi przedsiębiorców.', 'W JDG odpowiada właściciel.'],
      ['D', 'Bank nie odpowiada za długi klienta.', 'W JDG odpowiada właściciel.'],
    ],
  }),
  choice({
    id: 'bp-t-2',
    skill: 'biz-enterprise-types',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Firma zatrudnia 30 osób, a jej roczny obrót wynosi 6 mln euro. Do jakiej grupy przedsiębiorstw należy według wielkości?',
    choices: ['małych', 'mikro', 'średnich', 'dużych'],
    answer: 'A',
    hints: ['Jakie są progi zatrudnienia dla mikro, małych i średnich firm?', 'Mikro: do 9, małe: do 49, średnie: do 249 osób.', 'W którym przedziale mieści się 30 osób?', 'Obrót 6 mln euro też nie przekracza limitu dla tej grupy.'],
    steps: ['30 pracowników mieści się w przedziale 10–49.', 'To przedsiębiorstwo małe (obrót poniżej 10 mln euro).'],
    errors: [
      ['B', 'Mikro to do 9 pracowników.', '30 osób to małe przedsiębiorstwo.'],
      ['C', 'Średnie to od 50 pracowników.', '30 osób to małe przedsiębiorstwo.'],
      ['D', 'Duże to od 250 pracowników.', '30 osób to małe przedsiębiorstwo.'],
    ],
  }),
  choice({
    id: 'bp-t-3',
    skill: 'biz-enterprise-types',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Trzej programiści chcą założyć firmę, w której nie będą osobiście odpowiadać za jej długi, a na start mają łącznie 15 000 zł. Która forma prawna najlepiej pasuje?',
    choices: ['spółka z ograniczoną odpowiedzialnością', 'spółka jawna', 'spółka cywilna', 'spółka akcyjna'],
    answer: 'A',
    hints: ['W których spółkach wspólnicy odpowiadają własnym majątkiem?', 'W spółce cywilnej i jawnej.', 'Ile kapitału wymaga spółka akcyjna?', 'Co najmniej 100 000 zł — a spółka z o.o. tylko 5000 zł.'],
    steps: ['Spółka z o.o. co do zasady chroni prywatny majątek wspólników.', 'Wymaga kapitału min. 5000 zł — mieści się w ich budżecie; akcyjna wymaga 100 000 zł.'],
    errors: [
      ['B', 'W spółce jawnej wspólnicy odpowiadają całym majątkiem.', 'Ochronę daje spółka z o.o.'],
      ['C', 'W spółce cywilnej odpowiadają solidarnie całym majątkiem.', 'Ochronę daje spółka z o.o.'],
      ['D', 'Wymaga kapitału min. 100 000 zł.', 'Budżet wystarcza na spółkę z o.o.'],
    ],
  }),
  numeric({
    id: 'bp-t-4',
    skill: 'biz-enterprise-types',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Przedsiębiorca osiągnął przychód 200 000 zł przy kosztach 120 000 zł. W uproszczeniu (bez składek) porównaj ryczałt od przychodów 8,5% z podatkiem liniowym 19% od dochodu. O ile złotych niższy byłby podatek przy wyborze tańszej formy?',
    answer: 1800,
    verify: () => Math.abs(200000 * 0.085 - (200000 - 120000) * 0.19),
    hints: ['Od czego liczy się ryczałt, a od czego podatek liniowy?', 'Ryczałt od przychodu, liniowy od dochodu.', 'Ile wynosi dochód?', 'Przychód minus koszty — potem policz oba podatki i odejmij.'],
    steps: ['Ryczałt: 8,5% · 200 000 = 17 000 zł; liniowy: 19% · 80 000 = 15 200 zł.', 'Tańszy liniowy — o 1800 zł.'],
    errors: [['21000', 'Podatek liniowy policzony od przychodu.', 'Liniowy liczy się od dochodu.']],
  }),
  choice({
    id: 'bp-t-5',
    skill: 'biz-enterprise-types',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Spółdzielnia socjalna zatrudnia osoby długotrwale bezrobotne, prowadzi catering, a zysk przeznacza na ich aktywizację zawodową. Jak ją sklasyfikować?',
    choices: ['przedsiębiorstwo społeczne — prowadzi działalność gospodarczą w celu społecznym', 'organizacja charytatywna bez działalności gospodarczej', 'spółka kapitałowa nastawiona na maksymalizację zysku właścicieli', 'jednostka samorządu terytorialnego'],
    answer: 'A',
    hints: ['Czy spółdzielnia prowadzi działalność gospodarczą?', 'Tak — sprzedaje usługi cateringowe.', 'Na co przeznacza zysk?', 'Na cel społeczny — to cecha przedsiębiorstwa społecznego.'],
    steps: ['Spółdzielnia sprzedaje usługi — działa jak firma.', 'Zysk służy celowi społecznemu — to przedsiębiorstwo społeczne.'],
    errors: [
      ['B', 'Spółdzielnia sprzedaje usługi.', 'To działalność gospodarcza w celu społecznym.'],
      ['C', 'Zysk nie trafia do właścicieli.', 'Cel jest społeczny.'],
      ['D', 'Spółdzielnia nie jest częścią samorządu.', 'To przedsiębiorstwo społeczne.'],
    ],
  }),
  choice({
    id: 'bp-t-6',
    skill: 'biz-enterprise-types',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Grafik ma przychód 150 000 zł rocznie i bardzo niskie koszty (ok. 5000 zł). Rozważa ryczałt (12% od przychodu) albo podatek liniowy (19% od dochodu). Które stwierdzenie jest poprawne (w uproszczeniu, bez składek)?',
    choices: ['ryczałt, bo dochód jest prawie równy przychodowi', 'podatek liniowy, bo 19% liczy się od niższej kwoty', 'obie formy dadzą podobny podatek, więc to bez znaczenia', 'ryczałtu nie można wybrać przy przychodzie powyżej 100 000 zł'],
    answer: 'A',
    hints: ['Ile wynosi dochód przy tak niskich kosztach?', 'Prawie tyle, co przychód: 145 000 zł.', 'Policz oba podatki.', '12% · 150 000 i 19% · 145 000.'],
    steps: ['Ryczałt: 12% · 150 000 = 18 000 zł; liniowy: 19% · 145 000 = 27 550 zł.', 'Przy niskich kosztach ryczałt wypada korzystniej.'],
    errors: [
      ['B', 'Przy kosztach 5000 zł dochód jest prawie równy przychodowi.', 'Policz oba warianty: 18 000 zł i 27 550 zł.'],
      ['C', 'Różnica to ponad 9000 zł.', 'Policz oba warianty.'],
      ['D', 'Limit ryczałtu jest dużo wyższy.', 'Przychód 150 000 zł pozwala na ryczałt.'],
    ],
  }),

  // biz-business-model --------------------------------------------------------
  choice({
    id: 'bp-m-1',
    skill: 'biz-business-model',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Co to jest MVP (Minimum Viable Product)?',
    choices: ['najprostsza wersja produktu do sprawdzenia, czy klienci go chcą', 'pełna wersja produktu ze wszystkimi planowanymi funkcjami', 'produkt, który najlepiej sprzedaje się w ofercie firmy', 'ostateczna wersja produktu po wszystkich poprawkach'],
    answer: 'A',
    hints: ['Co oznacza słowo „minimum” w nazwie?', 'Najmniejszy zakres funkcji.', 'Po co firma wypuszcza taką wersję?', 'Żeby szybko sprawdzić reakcję klientów.'],
    steps: ['MVP to okrojona wersja produktu z najważniejszymi funkcjami.', 'Służy do szybkiego zbadania, czy klienci go potrzebują.'],
    errors: [
      ['B', 'MVP ma tylko najważniejsze funkcje.', 'MVP to najprostsza wersja do testów.'],
      ['C', 'Sprzedaż nie definiuje MVP.', 'MVP to najprostsza wersja do testów.'],
      ['D', 'MVP jest na początku, nie na końcu.', 'To wersja testowa.'],
    ],
  }),
  choice({
    id: 'bp-m-2',
    skill: 'biz-business-model',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Osoba zatrudniona przez sieć sklepów robi zakupy u konkurencji jak zwykły klient i ocenia jakość obsługi. Jak nazywa się ta metoda badań?',
    choices: ['tajemniczy klient', 'ankieta internetowa', 'analiza danych GUS', 'wywiad grupowy (fokusowy)'],
    answer: 'A',
    hints: ['Czy sprzedawca wie, że jest oceniany?', 'Nie — badacz udaje klienta.', 'Jakie słowo opisuje ukrytą rolę badacza?', 'Tajemniczy.'],
    steps: ['Badacz w roli zwykłego klienta obserwuje obsługę.', 'To metoda tajemniczego klienta (mystery shopping).'],
    errors: [
      ['B', 'Ankieta to pytania do respondentów.', 'Tu badacz udaje klienta.'],
      ['C', 'Dane GUS to badania wtórne.', 'Tu badacz udaje klienta.'],
      ['D', 'Wywiad fokusowy to dyskusja grupy.', 'Tu badacz udaje klienta.'],
    ],
  }),
  choice({
    id: 'bp-m-3',
    skill: 'biz-business-model',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Do którego bloku Business Model Canvas należy „umowa z dostawcą chmury obliczeniowej, na której działa aplikacja”?',
    choices: ['kluczowi partnerzy', 'segmenty klientów', 'strumienie przychodów', 'relacje z klientami'],
    answer: 'A',
    hints: ['Kim jest dostawca chmury dla firmy — klientem czy kimś, z kim współpracuje?', 'To zewnętrzny podmiot, bez którego firma nie działa.', 'Który blok opisuje takie podmioty?', 'Kluczowi partnerzy.'],
    steps: ['Dostawca kluczowej usługi to partner biznesowy.', 'W BMC należy do bloku „kluczowi partnerzy”.'],
    errors: [
      ['B', 'Dostawca nie jest klientem.', 'To kluczowy partner.'],
      ['C', 'Umowa z dostawcą to koszt, nie przychód.', 'To kluczowy partner.'],
      ['D', 'Relacje dotyczą klientów.', 'To kluczowy partner.'],
    ],
  }),
  choice({
    id: 'bp-m-4',
    skill: 'biz-business-model',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Które zdanie najlepiej opisuje propozycję wartości aplikacji do nauki matematyki dla maturzystów?',
    choices: ['„30 minut dziennie z zadaniami dobranymi do Twoich braków”', '„Zespół 10 doświadczonych programistów i nauczycieli”', '„Abonament 29 zł miesięcznie, pierwszy tydzień gratis”', '„Znajdziesz nas na Instagramie i w sklepie z aplikacjami”'],
    answer: 'A',
    hints: ['Na jakie pytanie odpowiada propozycja wartości?', 'Jaką korzyść klient dostaje i jaki problem rozwiązujemy?', 'Które zdanie mówi o korzyściach ucznia?', 'Pozostałe opisują firmę, cenę i kanał.'],
    steps: ['Propozycja wartości opisuje korzyści rozwiązujące problem klienta.', 'Liczba pracowników, cena i reklama to inne elementy modelu biznesu.'],
    errors: [
      ['B', 'To opis zasobów firmy.', 'Propozycja wartości mówi o korzyściach klienta.'],
      ['C', 'To model przychodów.', 'Propozycja wartości mówi o korzyściach klienta.'],
      ['D', 'To kanały dotarcia.', 'Propozycja wartości mówi o korzyściach klienta.'],
    ],
  }),
  text({
    id: 'bp-m-5',
    skill: 'biz-business-model',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Uzupełnij: porównywanie własnej firmy z najlepszymi w branży w celu przejęcia ich dobrych praktyk to ________.',
    answer: 'benchmarking',
    hints: ['Jak nazywa się „punkt odniesienia” po angielsku?', 'Benchmark.', 'Jak nazywa się proces porównywania się z nim?', 'Dodaj końcówkę „-ing”.'],
    steps: ['Porównywanie się z liderami to benchmarking.', 'Pozwala znaleźć i przenieść dobre praktyki.'],
    errors: [['marketing', 'Pomylone pojęcie.', 'Porównywanie z najlepszymi to benchmarking.']],
  }),
  choice({
    id: 'bp-m-6',
    skill: 'biz-business-model',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Założycielka startupu z dietetycznymi posiłkami na tydzień pokazała prostą stronę z menu i formularzem zamówień, zanim kupiła sprzęt. W dwa tygodnie zebrała 40 zamówień i uwagi klientów. Co zrobiła?',
    choices: ['przetestowała pomysł za pomocą MVP', 'przygotowała pełny biznesplan dla banku', 'przeprowadziła analizę makrootoczenia PEST', 'przeprowadziła badanie ankietowe rynku'],
    answer: 'A',
    hints: ['Czy miała już gotowy, pełny produkt?', 'Nie — tylko prostą stronę z zamówieniami.', 'Co chciała sprawdzić?', 'Czy klienci naprawdę chcą kupić — zanim wyda pieniądze.'],
    steps: ['Prosta wersja oferty pozwoliła sprawdzić popyt — to MVP.', 'Uwagi klientów posłużą do ulepszenia produktu.'],
    errors: [
      ['B', 'Nie opisano dokumentu dla banku.', 'To test pomysłu metodą MVP.'],
      ['C', 'PEST dotyczy makrootoczenia.', 'To test pomysłu metodą MVP.'],
      ['D', 'Ankieta zbiera deklaracje, a tu klienci naprawdę zamawiali.', 'To test pomysłu metodą MVP.'],
    ],
  }),
  choice({
    id: 'bp-m-7',
    skill: 'biz-business-model',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Który element NIE należy do typowej struktury biznesplanu?',
    choices: ['pełne treści umów z przyszłymi pracownikami', 'analiza rynku i konkurencji', 'plan finansowy z prognozą przychodów i kosztów', 'analiza ryzyka i plany awaryjne'],
    answer: 'A',
    hints: ['Czemu służy biznesplan?', 'Przekonaniu inwestora lub banku i zaplanowaniu działań.', 'Które elementy dotyczą rynku, pieniędzy i zagrożeń?', 'Jeden z elementów nie ma związku z przedsięwzięciem.'],
    steps: ['Biznesplan zawiera m.in. opis przedsięwzięcia, analizę rynku, plan marketingowy, organizację, plan finansowy i analizę ryzyka.', 'Treści umów z pracownikami to dokumenty wykonawcze, a nie część biznesplanu — plan opisuje najwyżej zatrudnienie i jego koszty.'],
    errors: [
      ['B', 'Analiza rynku to kluczowa część biznesplanu.', 'Szukamy elementu, który do niego nie należy.'],
      ['C', 'Plan finansowy to kluczowa część biznesplanu.', 'Szukamy elementu, który do niego nie należy.'],
      ['D', 'Analiza ryzyka należy do biznesplanu.', 'Szukamy elementu, który do niego nie należy.'],
    ],
  }),

  // biz-environment-swot ------------------------------------------------------
  choice({
    id: 'bp-s-1',
    skill: 'biz-environment-swot',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Które elementy analizy SWOT opisują czynniki WEWNĘTRZNE firmy?',
    choices: ['mocne i słabe strony', 'szanse i zagrożenia', 'mocne strony i szanse', 'słabe strony i zagrożenia'],
    answer: 'A',
    hints: ['Na które czynniki firma ma bezpośredni wpływ?', 'Na swoje zasoby, zespół, sprzęt.', 'Jak nazywają się w SWOT pozytywne i negatywne czynniki wewnętrzne?', 'S i W.'],
    steps: ['Mocne (S) i słabe (W) strony to czynniki wewnętrzne.', 'Szanse (O) i zagrożenia (T) to czynniki zewnętrzne.'],
    errors: [
      ['B', 'Szanse i zagrożenia są zewnętrzne.', 'Wewnętrzne to mocne i słabe strony.'],
      ['C', 'Szanse są zewnętrzne.', 'Wewnętrzne to mocne i słabe strony.'],
      ['D', 'Zagrożenia są zewnętrzne.', 'Wewnętrzne to mocne i słabe strony.'],
    ],
  }),
  choice({
    id: 'bp-s-2',
    skill: 'biz-environment-swot',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Firma produkująca rowery elektryczne zauważa, że samorządy budują coraz więcej ścieżek rowerowych. Gdzie w analizie SWOT wpisać ten czynnik?',
    choices: ['szanse', 'mocne strony', 'słabe strony', 'zagrożenia'],
    answer: 'A',
    hints: ['Czy firma ma wpływ na budowę ścieżek przez samorządy?', 'Nie — to czynnik zewnętrzny.', 'Czy ten czynnik pomaga firmie?', 'Tak — zwiększa zainteresowanie rowerami.'],
    steps: ['Budowa ścieżek to czynnik zewnętrzny i korzystny.', 'W SWOT to szansa.'],
    errors: [
      ['B', 'Mocne strony są wewnętrzne.', 'Czynnik zewnętrzny i korzystny to szansa.'],
      ['C', 'Słabe strony są wewnętrzne.', 'Czynnik zewnętrzny i korzystny to szansa.'],
      ['D', 'Czynnik jest korzystny.', 'To szansa.'],
    ],
  }),
  choice({
    id: 'bp-s-3',
    skill: 'biz-environment-swot',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Które zdarzenie należy do czynników TECHNOLOGICZNYCH w analizie PEST?',
    choices: ['upowszechnienie płatności telefonem i AI w obsłudze klienta', 'wzrost stóp procentowych podrażający kredyty na sprzęt', 'rosnąca liczba seniorów korzystających ze smartfonów', 'nowe przepisy o ochronie danych osobowych klientów'],
    answer: 'A',
    hints: ['Co oznaczają litery P, E, S, T?', 'Polityczno-prawne, ekonomiczne, społeczne, technologiczne.', 'Które zdarzenie dotyczy nowych technologii?', 'Pozostałe to czynniki ekonomiczne, społeczne i prawne.'],
    steps: ['Nowe technologie płatności i AI to czynnik technologiczny.', 'Stopy procentowe — ekonomiczny, liczba seniorów — społeczny (demografia), przepisy — polityczno-prawny.'],
    errors: [
      ['B', 'To czynnik ekonomiczny.', 'Technologiczny dotyczy nowych rozwiązań technicznych.'],
      ['C', 'To zmiana demograficzna — czynnik społeczny.', 'Technologiczny dotyczy nowych rozwiązań technicznych.'],
      ['D', 'Przepisy to czynnik polityczno-prawny.', 'Technologiczny dotyczy nowych rozwiązań technicznych.'],
    ],
  }),
  choice({
    id: 'bp-s-4',
    skill: 'biz-environment-swot',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Jaka forma ochrony własności intelektualnej najlepiej chroni nazwę i logo nowej marki napojów?',
    choices: ['znak towarowy', 'patent', 'prawo autorskie do programu komputerowego', 'wzór użytkowy'],
    answer: 'A',
    hints: ['Czy nazwa marki to wynalazek?', 'Nie — to oznaczenie odróżniające produkty.', 'Jaka ochrona dotyczy oznaczeń, np. logo?', 'Rejestruje się ją w Urzędzie Patentowym jako…'],
    steps: ['Nazwę i logo chroni znak towarowy.', 'Patent chroni wynalazki, wzór użytkowy — rozwiązania techniczne.'],
    errors: [
      ['B', 'Patent chroni wynalazki.', 'Nazwę i logo chroni znak towarowy.'],
      ['C', 'Program komputerowy to inny przedmiot ochrony.', 'Nazwę i logo chroni znak towarowy.'],
      ['D', 'Wzór użytkowy dotyczy rozwiązań technicznych.', 'Nazwę i logo chroni znak towarowy.'],
    ],
  }),
  choice({
    id: 'bp-s-5',
    skill: 'biz-environment-swot',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Szkoła językowa ma doświadczonych lektorów (mocna strona), a rośnie liczba osób uczących się zdalnie (szansa). Która strategia wynika z analizy SWOT?',
    choices: ['uruchomienie kursów online prowadzonych przez doświadczonych lektorów', 'zamknięcie szkoły, bo rośnie konkurencja internetowa', 'zwolnienie lektorów i zatrudnienie studentów', 'ograniczenie oferty do zajęć stacjonarnych'],
    answer: 'A',
    hints: ['Jak połączyć mocną stronę z szansą?', 'Wykorzystać atut firmy, żeby skorzystać z trendu.', 'Czego dotyczy szansa?', 'Nauki zdalnej — więc potrzebna jest oferta online.'],
    steps: ['Strategia SO: wykorzystaj mocne strony, żeby zrealizować szansę.', 'Doświadczeni lektorzy mogą prowadzić kursy online.'],
    errors: [
      ['B', 'Szansa została potraktowana jak zagrożenie.', 'Wykorzystaj mocne strony i szansę.'],
      ['C', 'Rezygnuje z mocnej strony.', 'Wykorzystaj mocne strony i szansę.'],
      ['D', 'Ignoruje szansę.', 'Wykorzystaj mocne strony i szansę.'],
    ],
  }),
  choice({
    id: 'bp-s-6',
    skill: 'biz-environment-swot',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Które źródło przewagi konkurencyjnej jest NAJTRUDNIEJSZE do skopiowania przez konkurencję?',
    choices: ['opatentowana technologia i zgrany, doświadczony zespół', 'niższa cena wprowadzona w promocji na tydzień', 'nowe, wyróżniające się opakowanie produktu', 'wydłużone godziny otwarcia wszystkich sklepów'],
    answer: 'A',
    hints: ['Czy konkurent może łatwo obniżyć cenę albo zmienić opakowanie?', 'Tak — w kilka dni.', 'Co chroni prawo i czego nie da się kupić z dnia na dzień?', 'Patentów i doświadczenia zespołu.'],
    steps: ['Trwała przewaga opiera się na zasobach trudnych do skopiowania.', 'Patent chroni prawo, a zespół i doświadczenie budowały się latami.'],
    errors: [
      ['B', 'Promocję konkurent skopiuje od razu.', 'Trwała przewaga jest trudna do skopiowania.'],
      ['C', 'Opakowanie łatwo zmienić.', 'Trwała przewaga jest trudna do skopiowania.'],
      ['D', 'Godziny otwarcia łatwo zmienić.', 'Trwała przewaga jest trudna do skopiowania.'],
    ],
  }),

  // biz-profitability ---------------------------------------------------------
  choice({
    id: 'bp-r-1',
    skill: 'biz-profitability',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Który koszt piekarni jest kosztem ZMIENNYM?',
    choices: ['mąka zużyta do wypieku', 'czynsz za lokal', 'rata leasingu pieca', 'pensja księgowej na etacie'],
    answer: 'A',
    hints: ['Który koszt rośnie z każdym upieczonym bochenkiem?', 'Ten, który zależy od wielkości produkcji.', 'Czy czynsz się zmienia, gdy upieczesz więcej?', 'Nie — to koszt stały.'],
    steps: ['Zużycie mąki rośnie wraz z produkcją — koszt zmienny.', 'Czynsz, leasing i pensja stała nie zależą od liczby bochenków.'],
    errors: [
      ['B', 'Czynsz nie zależy od produkcji.', 'Koszt zmienny rośnie z produkcją.'],
      ['C', 'Rata leasingu jest stała.', 'Koszt zmienny rośnie z produkcją.'],
      ['D', 'Stała pensja nie zależy od produkcji.', 'Koszt zmienny rośnie z produkcją.'],
    ],
  }),
  numeric({
    id: 'bp-r-2',
    skill: 'biz-profitability',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Cena produktu wynosi 50 zł, a koszt zmienny jednej sztuki 20 zł. Ile wynosi marża jednostkowa (na pokrycie kosztów stałych) w zł?',
    answer: 30,
    verify: () => 50 - 20,
    hints: ['Co zostaje ze sprzedaży jednej sztuki po pokryciu jej kosztu zmiennego?', 'Różnica ceny i kosztu zmiennego.', 'Na co idzie ta kwota?', 'Na pokrycie kosztów stałych, a potem na zysk.'],
    steps: ['Marża jednostkowa = cena − koszt zmienny.', '50 − 20 = 30 zł.'],
    errors: [['70', 'Koszt dodany zamiast odjęty.', 'Marża to cena minus koszt zmienny.']],
  }),
  numeric({
    id: 'bp-r-3',
    skill: 'biz-profitability',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Koszty stałe firmy wynoszą 12 000 zł miesięcznie, cena produktu 50 zł, a koszt zmienny jednej sztuki 20 zł. Oblicz ilościowy próg rentowności (w sztukach miesięcznie).',
    answer: 400,
    verify: () => 12000 / (50 - 20),
    hints: ['Ile każda sztuka „dokłada” do pokrycia kosztów stałych?', 'Marżę jednostkową: cena minus koszt zmienny.', 'Ile takich marż potrzeba, by pokryć koszty stałe?', 'Podziel koszty stałe przez marżę jednostkową.'],
    steps: ['Marża jednostkowa: 50 − 20 = 30 zł.', 'Próg: 12 000 : 30 = 400 sztuk.'],
    errors: [['240', 'Koszty stałe podzielone przez cenę.', 'Dzieli się przez marżę jednostkową.']],
  }),
  numeric({
    id: 'bp-r-4',
    skill: 'biz-profitability',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Koszty stałe: 12 000 zł miesięcznie, cena: 50 zł, koszt zmienny: 20 zł za sztukę. Firma sprzedała 600 sztuk. Oblicz rentowność sprzedaży (zysk : przychody, w %).',
    answer: 20,
    verify: () => ((600 * (50 - 20) - 12000) / (600 * 50)) * 100,
    hints: ['Ile wynoszą przychody?', '600 · 50 zł.', 'Ile wynosi zysk?', 'Marża ze wszystkich sztuk minus koszty stałe — potem podziel przez przychody.'],
    steps: ['Przychody: 30 000 zł; zysk: 600 · 30 − 12 000 = 6000 zł.', 'Rentowność: 6000 : 30 000 · 100% = 20%.'],
    errors: [['50', 'Zysk odniesiony do kosztów stałych.', 'Rentowność sprzedaży liczy się względem przychodów.']],
  }),
  numeric({
    id: 'bp-r-5',
    skill: 'biz-profitability',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Koszty stałe: 12 000 zł miesięcznie, cena: 50 zł, koszt zmienny: 20 zł za sztukę. Ile sztuk trzeba sprzedać, żeby osiągnąć zysk 9000 zł miesięcznie?',
    answer: 700,
    verify: () => (12000 + 9000) / (50 - 20),
    hints: ['Co musi pokryć marża ze sprzedaży?', 'Koszty stałe i zakładany zysk.', 'Ile wynosi marża jednostkowa?', 'Podziel sumę kosztów stałych i zysku przez marżę.'],
    steps: ['Potrzebna marża łączna: 12 000 + 9000 = 21 000 zł.', 'Liczba sztuk: 21 000 : 30 = 700.'],
    errors: [['300', 'Pominięte koszty stałe.', 'Marża musi pokryć koszty stałe i zysk.']],
  }),
  numeric({
    id: 'bp-r-6',
    skill: 'biz-profitability',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Koszty stałe wynoszą 12 000 zł miesięcznie, a koszt zmienny 20 zł za sztukę. Firma realnie może sprzedać 300 sztuk miesięcznie. Jaka jest najniższa cena, przy której nie poniesie straty (w zł)?',
    answer: 60,
    verify: () => 20 + 12000 / 300,
    hints: ['Jaki warunek musi spełnić cena, żeby firma wyszła na zero przy 300 sztukach?', 'Przychód musi pokryć koszty stałe i zmienne.', 'Ile kosztów stałych przypada na jedną sztukę?', 'Cena = koszt zmienny + koszty stałe : 300.'],
    steps: ['Koszty stałe na sztukę: 12 000 : 300 = 40 zł.', 'Cena progowa: 20 + 40 = 60 zł.'],
    errors: [['40', 'Pominięty koszt zmienny.', 'Cena musi pokryć też koszt zmienny sztuki.']],
  }),
  text({
    id: 'bp-r-7',
    skill: 'biz-profitability',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Uzupełnij: wielkość sprzedaży, przy której przychody są równe kosztom całkowitym, a firma nie osiąga ani zysku, ani straty, to próg ________.',
    answer: 'rentowności',
    variants: ['rentownosci'],
    hints: ['Jak nazywa się punkt „wyjścia na zero”?', 'Próg…', 'Od tego punktu firma zaczyna być rentowna.', 'Rzeczownik od słowa „rentowny”.'],
    steps: ['Punkt, w którym zysk wynosi 0, to próg rentowności.', 'Powyżej niego firma osiąga zysk.'],
    errors: [['opłacalności', 'Nieprecyzyjny termin.', 'Poprawny termin: próg rentowności.']],
  }),

  // biz-financing-risk --------------------------------------------------------
  choice({
    id: 'bp-f-1',
    skill: 'biz-financing-risk',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Które źródło finansowania jest źródłem OBCYM?',
    choices: ['kredyt bankowy', 'oszczędności właściciela', 'zysk zatrzymany w firmie', 'wkład wspólnika do spółki'],
    answer: 'A',
    hints: ['Czym różni się źródło własne od obcego?', 'Obce trzeba zwrócić innemu podmiotowi.', 'Które pieniądze należą do firmy lub jej właścicieli?', 'Oszczędności, zysk i wkłady — kredyt trzeba spłacić bankowi.'],
    steps: ['Kredyt to pieniądze banku — trzeba je oddać z odsetkami.', 'Oszczędności, zysk zatrzymany i wkłady to źródła własne.'],
    errors: [
      ['B', 'To źródło własne.', 'Obce jest kredyt.'],
      ['C', 'To źródło własne.', 'Obce jest kredyt.'],
      ['D', 'To źródło własne.', 'Obce jest kredyt.'],
    ],
  }),
  choice({
    id: 'bp-f-2',
    skill: 'biz-financing-risk',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Młoda firma technologiczna otrzymała od zamożnej osoby prywatnej 300 000 zł w zamian za 20% udziałów oraz wsparcie w postaci doradztwa. Kim jest ten inwestor?',
    choices: ['aniołem biznesu', 'bankiem', 'urzędem pracy', 'franczyzodawcą'],
    answer: 'A',
    hints: ['Czy inwestor pożyczył pieniądze, czy kupił udziały?', 'Kupił udziały.', 'Czy to instytucja, czy osoba prywatna?', 'Osoba prywatna, która dodatkowo doradza.'],
    steps: ['Prywatny inwestor dający kapitał za udziały i wsparcie merytoryczne to anioł biznesu.', 'Bank udziela kredytów, urząd pracy — dotacji.'],
    errors: [
      ['B', 'Bank pożycza, nie kupuje udziałów.', 'To anioł biznesu.'],
      ['C', 'Urząd pracy daje dotacje.', 'To anioł biznesu.'],
      ['D', 'Franczyzodawca udostępnia markę.', 'To anioł biznesu.'],
    ],
  }),
  choice({
    id: 'bp-f-3',
    skill: 'biz-financing-risk',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Jaka jest główna rola inkubatora przedsiębiorczości?',
    choices: ['wsparcie nowych firm: tanie biuro, doradztwo, kontakty', 'rejestrowanie nowych firm w CEIDG i KRS', 'nadzór nad przestrzeganiem prawa przez nowe firmy', 'finansowanie firm w zamian za większość udziałów'],
    answer: 'A',
    hints: ['Z czym kojarzy się słowo „inkubator”?', 'Z miejscem, w którym coś młodego rośnie w bezpiecznych warunkach.', 'Czego potrzebuje młoda firma oprócz pieniędzy?', 'Miejsca, wiedzy i kontaktów.'],
    steps: ['Inkubator pomaga firmom na starcie: biuro, doradztwo, sieć kontaktów.', 'Rejestracja, kontrole i finansowanie za udziały to zadania innych instytucji.'],
    errors: [
      ['B', 'Rejestracją zajmują się urzędy i sądy rejestrowe.', 'Inkubator wspiera młode firmy.'],
      ['C', 'Kontrole prowadzą urzędy, np. skarbowy.', 'Inkubator wspiera młode firmy.'],
      ['D', 'Tak działa inwestor, np. fundusz — nie inkubator.', 'Inkubator wspiera młode firmy usługami i doradztwem.'],
    ],
  }),
  numeric({
    id: 'bp-f-4',
    skill: 'biz-financing-risk',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Ryzyko awarii kluczowej maszyny w ciągu roku oceniono na 25%, a jej skutek finansowy na 80 000 zł. Oblicz oczekiwaną stratę z tego ryzyka (w zł).',
    answer: 20000,
    verify: () => 0.25 * 80000,
    hints: ['Jak połączyć prawdopodobieństwo ze skutkiem?', 'Oczekiwana strata = prawdopodobieństwo · skutek.', 'Zamień 25% na ułamek dziesiętny.', 'Pomnóż 0,25 przez 80 000.'],
    steps: ['Oczekiwana strata = 0,25 · 80 000 zł.', '= 20 000 zł.'],
    errors: [['80000', 'Pominięte prawdopodobieństwo.', 'Skutek mnoży się przez prawdopodobieństwo.']],
  }),
  choice({
    id: 'bp-f-5',
    skill: 'biz-financing-risk',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Firma kupiła ubezpieczenie od pożaru magazynu. Jaki to sposób postępowania z ryzykiem?',
    choices: ['przeniesienie ryzyka', 'unikanie ryzyka', 'akceptacja ryzyka', 'zwiększenie ryzyka'],
    answer: 'A',
    hints: ['Kto poniesie koszty pożaru po zakupie polisy?', 'Głównie ubezpieczyciel.', 'Czy firma usunęła przyczynę ryzyka?', 'Nie — przekazała skutki finansowe komuś innemu.'],
    steps: ['Ubezpieczenie przenosi skutki finansowe ryzyka na ubezpieczyciela.', 'To przeniesienie ryzyka.'],
    errors: [
      ['B', 'Unikanie to rezygnacja z działania, które niesie ryzyko.', 'Ubezpieczenie to przeniesienie ryzyka.'],
      ['C', 'Akceptacja to świadome pozostawienie ryzyka.', 'Ubezpieczenie to przeniesienie ryzyka.'],
      ['D', 'Ubezpieczenie nie zwiększa ryzyka.', 'To przeniesienie ryzyka.'],
    ],
  }),
  choice({
    id: 'bp-f-6',
    skill: 'biz-financing-risk',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Założycielka startupu wybiera między kredytem bankowym na 500 000 zł a inwestycją funduszu venture capital za 30% udziałów. Które stwierdzenie trafnie opisuje różnicę?',
    choices: ['kredyt: raty z odsetkami, ale pełna własność; VC: bez rat, ale oddanie udziałów', 'kredyt: oddanie bankowi 30% udziałów; VC: spłata w ratach z odsetkami', 'kredyt: raty z odsetkami; VC: pieniądze bez żadnych warunków', 'obie opcje dają pieniądze bez utraty kontroli i bez kosztów'],
    answer: 'A',
    hints: ['Co bank dostaje w zamian za kredyt?', 'Spłatę z odsetkami.', 'Co dostaje fundusz VC?', 'Udziały w firmie i część przyszłych zysków.'],
    steps: ['Kredyt: obowiązek spłaty, ale bez oddawania własności.', 'VC: brak rat, ale utrata części udziałów i kontroli.'],
    errors: [
      ['B', 'Pomylone role: bank nie obejmuje udziałów, a VC nie udziela kredytu.', 'Kredyt spłaca się z odsetkami, VC obejmuje udziały.'],
      ['C', 'VC obejmuje udziały i wpływ na decyzje.', 'Każde finansowanie ma koszt.'],
      ['D', 'Każda opcja ma koszt: odsetki albo udziały.', 'Kredyt spłaca się z odsetkami, VC obejmuje udziały.'],
    ],
  }),
];

// ===========================================================================
// Fiszki
// ===========================================================================

export const FIRM_CARDS: Flashcard[] = [
  card('c-bp-t-1', 'biz-enterprise-types', 'definicja', 'Spółka z o.o. — kapitał i odpowiedzialność?', 'Min. 5000 zł; wspólnicy co do zasady nie odpowiadają za długi spółki.'),
  card('c-bp-t-2', 'biz-enterprise-types', 'pulapka', 'Ryczałt — od czego?', 'Od przychodu (koszty nie obniżają podatku).'),

  card('c-bp-m-1', 'biz-business-model', 'definicja', 'MVP?', 'Najprostsza wersja produktu do sprawdzenia, czy klienci go chcą.'),
  card('c-bp-m-2', 'biz-business-model', 'definicja', 'Ile bloków ma Business Model Canvas?', '9: klienci, propozycja wartości, kanały, relacje, przychody, zasoby, działania, partnerzy, koszty.'),

  card('c-bp-s-1', 'biz-environment-swot', 'metoda', 'SWOT — wewnętrzne czy zewnętrzne?', 'S i W: wewnętrzne. O i T: zewnętrzne.'),
  card('c-bp-s-2', 'biz-environment-swot', 'definicja', 'Co chroni znak towarowy?', 'Oznaczenia odróżniające produkty: nazwę, logo.'),

  card('c-bp-r-1', 'biz-profitability', 'wzor', 'Próg rentowności ilościowy w modelu liniowym?', '$Q = \\frac{K_{\\text{stałe}}}{c-k_{zm}}$ przy stałej cenie c i jednostkowym koszcie zmiennym, gdy $c>k_{zm}$. Przy dodatnich kosztach stałych i $c\\le k_{zm}$ nie ma dodatniego progu pokrycia kosztów.'),
  card('c-bp-r-2', 'biz-profitability', 'wzor', 'Rentowność sprzedaży?', 'zysk : przychody · 100%.'),

  card('c-bp-f-1', 'biz-financing-risk', 'definicja', 'Anioł biznesu?', 'Prywatny inwestor: kapitał i doradztwo w zamian za udziały.'),
  card('c-bp-f-2', 'biz-financing-risk', 'wzor', 'Oczekiwana strata z ryzyka?', 'prawdopodobieństwo · skutek finansowy.'),
];
